import React, { useState, useEffect } from 'react';
import { usePageConfig } from '../../contexts/PageConfigContext';
import { ordersApi } from '../../services/api';
import { OrderStatistics as OrderStatisticsData } from '../../types/statistics';
import { OrderStatsCard } from './components/OrderStatsCard';
import { ProductSalesTable } from './components/ProductSalesTable';
import { OrderBreakdownList } from './components/OrderBreakdownList';
import { ShoppingCart, Package, TrendingUp, Calendar, Printer } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

type DatePreset = 'today' | 'yesterday' | 'week' | 'month' | 'custom';

export const OrderStatistics: React.FC = () => {
  const { setPageConfig } = usePageConfig();
  const [statistics, setStatistics] = useState<OrderStatisticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedPreset, setSelectedPreset] = useState<DatePreset>('today');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');

  // Initialize dates based on preset
  useEffect(() => {
    const today = new Date();
    const getDateString = (date: Date) => date.toISOString().split('T')[0];

    switch (selectedPreset) {
      case 'today':
        setDateFrom(getDateString(today));
        setDateTo(getDateString(today));
        break;
      case 'yesterday':
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        setDateFrom(getDateString(yesterday));
        setDateTo(getDateString(yesterday));
        break;
      case 'week':
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - today.getDay());
        setDateFrom(getDateString(weekStart));
        setDateTo(getDateString(today));
        break;
      case 'month':
        const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
        setDateFrom(getDateString(monthStart));
        setDateTo(getDateString(today));
        break;
      case 'custom':
        // Keep current custom dates
        break;
    }
  }, [selectedPreset]);

  // Load statistics
  useEffect(() => {
    const loadStatistics = async () => {
      if (!dateFrom || !dateTo) return;

      setLoading(true);
      try {
        const data = await ordersApi.getStatistics({
          dateFrom,
          dateTo,
        });
        setStatistics(data);
      } catch (error) {
        console.error('Error loading statistics:', error);
      } finally {
        setLoading(false);
      }
    };

    loadStatistics();
  }, [dateFrom, dateTo]);

  // Set page config
  useEffect(() => {
    setPageConfig({
      title: 'Estadísticas de Ventas',
      description: 'Análisis de ventas y productos',
      actions: [], // Explicitly empty to hide TopBar
    });
  }, [setPageConfig]);

  const totalUnits = statistics?.productSales.reduce((sum, p) => sum + p.totalQuantity, 0) || 0;

  return (
    <div className="space-y-6">
      {/* Header and Actions */}
      <div className="print:hidden">
        {/* Date Filter and Print Button in one line */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 w-full">
          <div className="flex items-center justify-between gap-4">
            {/* Date Range Picker */}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="gap-2">
                  <Calendar size={18} />
                  {selectedPreset === 'custom' 
                    ? `${new Date(dateFrom).toLocaleDateString('es-ES')} - ${new Date(dateTo).toLocaleDateString('es-ES')}`
                    : selectedPreset === 'today' ? 'Hoy'
                    : selectedPreset === 'yesterday' ? 'Ayer'
                    : selectedPreset === 'week' ? 'Esta Semana'
                    : 'Este Mes'
                  }
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-4" align="start">
                <div className="space-y-3">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Período</Label>
                    <div className="grid grid-cols-2 gap-2">
                      <Button
                        size="sm"
                        variant={selectedPreset === 'today' ? 'default' : 'outline'}
                        onClick={() => setSelectedPreset('today')}
                        className="w-full"
                      >
                        Hoy
                      </Button>
                      <Button
                        size="sm"
                        variant={selectedPreset === 'yesterday' ? 'default' : 'outline'}
                        onClick={() => setSelectedPreset('yesterday')}
                        className="w-full"
                      >
                        Ayer
                      </Button>
                      <Button
                        size="sm"
                        variant={selectedPreset === 'week' ? 'default' : 'outline'}
                        onClick={() => setSelectedPreset('week')}
                        className="w-full"
                      >
                        Esta Semana
                      </Button>
                      <Button
                        size="sm"
                        variant={selectedPreset === 'month' ? 'default' : 'outline'}
                        onClick={() => setSelectedPreset('month')}
                        className="w-full"
                      >
                        Este Mes
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Button
                      size="sm"
                      variant={selectedPreset === 'custom' ? 'default' : 'outline'}
                      onClick={() => setSelectedPreset('custom')}
                      className="w-full"
                    >
                      Personalizado
                    </Button>
                    
                    {selectedPreset === 'custom' && (
                      <div className="space-y-3 pt-2">
                        <div className="space-y-1.5">
                          <Label htmlFor="date-from" className="text-xs">Desde</Label>
                          <Input
                            id="date-from"
                            type="date"
                            value={dateFrom}
                            onChange={(e) => setDateFrom(e.target.value)}
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label htmlFor="date-to" className="text-xs">Hasta</Label>
                          <Input
                            id="date-to"
                            type="date"
                            value={dateTo}
                            onChange={(e) => setDateTo(e.target.value)}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </PopoverContent>
            </Popover>

            {/* Print Button */}
            <Button
              onClick={() => window.print()}
              variant="outline"
              className="gap-2"
            >
              <Printer size={18} />
              Imprimir
            </Button>
          </div>
        </div>
      </div>

      {/* Print Header */}
      <div className="hidden print:block mb-4">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Desglose de Ventas</h1>
        <p className="text-gray-600">
          Período: {new Date(dateFrom).toLocaleDateString()} - {new Date(dateTo).toLocaleDateString()}
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
        </div>
      ) : statistics ? (
        <>
          {/* Summary Cards - Hidden on Print */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 print:hidden">
            <OrderStatsCard
              title="Total Ventas"
              value={statistics.totalOrders}
              icon={ShoppingCart}
              color="green"
            />
            <OrderStatsCard
              title="Productos Únicos"
              value={statistics.totalProducts}
              icon={Package}
              color="blue"
            />
            <OrderStatsCard
              title="Unidades Totales"
              value={totalUnits}
              icon={TrendingUp}
              color="purple"
            />
          </div>

          {/* Order Breakdown */}
          <OrderBreakdownList orderBreakdowns={statistics.orderBreakdowns} />

          {/* Product Sales Table - Hidden on Print */}
          <div className="print:hidden">
            <ProductSalesTable productSales={statistics.productSales} />
          </div>
        </>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
          <p className="text-gray-500">No hay datos disponibles</p>
        </div>
      )}
    </div>
  );
};
